import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

/**
 * A taxa SISP de um evento por meio de pagamento: Vinti4, Cartão guardado, Internacional
 * (pedido da Linda, 20/10/2026). SISP é o que a SISP cobrou; Pagali é o que se cobra ao
 * promotor pelo contrato. Interno: tem custo e margem, não vai ao promotor.
 */
export interface LinhaMeio { trx: number; valorCVE: number; feeCVE: number; custoCVE?: number; taxaBps: number | null }

export const NOME_MEIO: Record<string, string> = {
  VINTI4: 'Vinti4', CARTAO_GUARDADO: 'Cartão guardado (Vinti4)', INTERNACIONAL: 'Internacional (Visa, Mastercard, Amex)',
};
export const ORDEM_MEIOS = ['VINTI4', 'CARTAO_GUARDADO', 'INTERNACIONAL'];

const cve = (n: number) => `${Math.round(Number(n) || 0).toLocaleString('pt-PT')}$`;
const pct = (v: number) => `${v.toLocaleString('pt-PT', { maximumFractionDigits: 2 })}%`;

export default function TabelaMeios({ porMeio, ivaBps, comIva, comMargem = true }: {
  porMeio: Record<string, LinhaMeio>; ivaBps: number; comIva: boolean;
  /** Sem margem a taxa cobrada é o custo SISP: mostra-se só ele. */
  comMargem?: boolean;
}) {
  const linhas = ORDEM_MEIOS.filter((m) => porMeio[m]).map((m) => ({ m, ...porMeio[m] }));
  if (!linhas.length) {
    return <Typography variant="body2" color="text.secondary">Sem pagamentos SISP casados na Reconciliação.</Typography>;
  }
  // IVA só sobre a margem (receita da Pagali); o custo SISP já traz o IVA da SISP.
  const iva = (fee: number, custo: number) => (comIva ? Math.round((Math.max(0, fee - custo) * ivaBps) / 10000) : 0);
  const tot = linhas.reduce((a, l) => ({
    trx: a.trx + l.trx, valor: a.valor + l.valorCVE, custo: a.custo + Number(l.custoCVE || 0), fee: a.fee + l.feeCVE,
  }), { trx: 0, valor: 0, custo: 0, fee: 0 });

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Meio de pagamento</TableCell>
          <TableCell align="right">Pagamentos</TableCell>
          <TableCell align="right">Valor</TableCell>
          <TableCell align="right">{comMargem ? 'Custo SISP' : 'Taxa SISP'}</TableCell>
          {comMargem && <>
            <TableCell align="right">Taxa ao promotor</TableCell>
            <TableCell align="right">Taxa cartões ao promotor{comIva ? ' (+ IVA da margem)' : ''}</TableCell>
            <TableCell align="right">Margem</TableCell>
          </>}
        </TableRow>
      </TableHead>
      <TableBody>
        {linhas.map((l) => {
          // Sem taxa no contrato o cobrado é o valor real: mostra-se a percentagem efectiva.
          const taxa = l.taxaBps != null ? l.taxaBps / 100 : (l.valorCVE ? (l.feeCVE / l.valorCVE) * 100 : 0);
          return (
            <TableRow key={l.m}>
              <TableCell>{NOME_MEIO[l.m] ?? l.m}</TableCell>
              <TableCell align="right">{l.trx}</TableCell>
              <TableCell align="right">{cve(l.valorCVE)}</TableCell>
              <TableCell align="right">
                {cve(Number(l.custoCVE || 0))}
                <Typography variant="caption" color="text.secondary" component="div">
                  {l.valorCVE ? pct((Number(l.custoCVE || 0) / l.valorCVE) * 100) : '—'}
                </Typography>
              </TableCell>
              {comMargem && <>
                <TableCell align="right">
                  {pct(taxa)}
                  <Typography variant="caption" color="text.secondary" component="div">
                    {l.taxaBps != null ? 'contrato' : 'valor real'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {cve(l.feeCVE + iva(l.feeCVE, Number(l.custoCVE || 0)))}
                  {comIva && <Typography variant="caption" color="text.secondary" component="div">{cve(l.feeCVE)} + IVA {cve(iva(l.feeCVE, Number(l.custoCVE || 0)))}</Typography>}
                </TableCell>
                <TableCell align="right">{cve(l.feeCVE - Number(l.custoCVE || 0))}</TableCell>
              </>}
            </TableRow>
          );
        })}
        <TableRow>
          <TableCell><b>Total</b></TableCell>
          <TableCell align="right"><b>{tot.trx}</b></TableCell>
          <TableCell align="right"><b>{cve(tot.valor)}</b></TableCell>
          <TableCell align="right"><b>{cve(tot.custo)}</b></TableCell>
          {comMargem && <>
            <TableCell />
            <TableCell align="right"><b>{cve(tot.fee + iva(tot.fee, tot.custo))}</b></TableCell>
            <TableCell align="right"><b>{cve(tot.fee - tot.custo)}</b></TableCell>
          </>}
        </TableRow>
      </TableBody>
    </Table>
  );
}
